import React, { Suspense } from 'react';
import classNames from 'classnames';
import { CampaignifyBlock } from './Blocks/CampaignifyBlock';
import { OptimizelyCampaignifyBlock } from './Blocks/OptimizelyCampaignifyBlock';
import { InContentSlot } from './InContentSlot';
import { ArticleData } from '@benzinga/article-manager';
import type { Block as BlockInterface } from '@benzinga/content-manager';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { AD_RESERVED_CLASS, IN_CONTENT_SLOT_BLOCK_NAME, isDisplayAdBlockName } from '@benzinga/ads-utils';
import { ReadNextBlock } from '@benzinga/blocks/read-next';
import { ConnatixBlock } from '@benzinga/blocks/connatix';
import { ConnatixLiveBlock } from '@benzinga/blocks/connatix-live';

const MoneyBlock = React.lazy(() => import('@benzinga/money/block').then(module => ({ default: module.MoneyBlock })));

const Primis = React.lazy(() =>
  import('@benzinga/ads/primis').then(module => {
    return { default: module.Primis };
  }),
);

const EAGER_HTML_BLOCK_NAMES = new Set([
  'core/heading',
  'core/list',
  'core/paragraph',
  'core/table',
  'tadv/classic-paragraph',
]);

const PARSED_HTML_BLOCK_NAMES = new Set(['core/html', 'core/separator', 'core/spacer']);
const EAGER_HTML_TAGS = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'ol', 'p', 'table', 'ul']);

const renderEagerHtmlBlock = (block: {
  html?: string;
  innerHTML?: string;
  tag?: string;
  tagAttributes?: { class?: string; className?: string; id?: string };
  id?: string;
}) => {
  const tag = block.tag || 'p';
  const className =
    block.tagAttributes?.className ||
    block.tagAttributes?.class ||
    (EAGER_HTML_TAGS.has(tag) ? 'block core-block' : undefined);
  const html = block.html ?? block.innerHTML ?? '';
  return React.createElement(tag, {
    className,
    ...(block.id || block.tagAttributes?.id ? { id: block.id || block.tagAttributes?.id } : {}),
    ...(html ? { dangerouslySetInnerHTML: { __html: sanitizeHTML(html) } } : {}),
  });
};

const moneyBlockFallback = (block: { blockName?: string } | null | undefined) => {
  if (isDisplayAdBlockName(block?.blockName)) {
    return <div className={classNames(AD_RESERVED_CLASS, 'min-h-[250px] w-full')} />;
  }
  if (block?.blockName === 'acf/connatix-live-block') {
    return <div aria-hidden className="min-h-[170px] w-full" />;
  }
  return <div />;
};

export interface PrimisArticleBlock extends BlockInterface {
  id: string;
}

export interface ArticleBlockProps {
  block: any;
  node?: ArticleData;
}

export interface CampaignifyPlaceholderBlock extends BlockInterface {
  position: string;
  nodeID: number | string;
}

export type ArticleBlockType = BlockInterface | PrimisArticleBlock | CampaignifyPlaceholderBlock;

export const ArticleBlock: React.FC<ArticleBlockProps> = props => {
  const resolved = getArticleBlock(props);
  if (resolved) return resolved;
  return (
    <Suspense fallback={moneyBlockFallback(props.block)}>
      <MoneyBlock {...props} type="article-in-content" />
    </Suspense>
  );
};

export const getArticleBlock = ({
  block,
}: // node,
ArticleBlockProps) => {
  if (block.blockName === 'ad/campaign') {
    return <div className="bz-campaign" dangerouslySetInnerHTML={{ __html: sanitizeHTML(block.value) }} />;
  } else if (block.blockName === 'ad/primis') {
    return (
      <Suspense fallback={<div className="min-h-[170px] w-full" />}>
        <Primis id={block.id} placeholderId="primis-article-video" renderOnEvent selector="#primis-article-video" />
      </Suspense>
    );
  } else if (block.blockName === 'ad/campaignify') {
    return <CampaignifyBlock nodeID={block.nodeID} placeholderId={block.position} ticker={block.ticker} />;
  } else if (block.blockName === 'ad/optimizely-campaignify') {
    return <OptimizelyCampaignifyBlock nodeID={block.nodeID} placeholderId={block.position} />;
  } else if (block.blockName === IN_CONTENT_SLOT_BLOCK_NAME) {
    return <InContentSlot block={block} />;
  } else if (block.blockName === 'acf/read-next') {
    return <ReadNextBlock {...block} />;
  } else if (block.blockName === 'acf/connatix-block') {
    return <ConnatixBlock {...block} />;
  } else if (block.blockName === 'acf/connatix-live-block') {
    return <ConnatixLiveBlock {...block} />;
  } else if (block.blockName === 'core/image') {
    const src = block.tagAttributes?.src;
    const html = block.html ?? block.innerHTML ?? '';
    if (src) {
      return (
        <img
          alt={block.tagAttributes?.alt || ''}
          className="core-image block"
          height={block.tagAttributes?.height}
          src={src}
          width={block.tagAttributes?.width}
        />
      );
    }
    return html ? <div className="core-image block" dangerouslySetInnerHTML={{ __html: sanitizeHTML(html) }} /> : null;
  } else if (
    EAGER_HTML_BLOCK_NAMES.has(block.blockName ?? '') ||
    (PARSED_HTML_BLOCK_NAMES.has(block.blockName ?? '') && EAGER_HTML_TAGS.has(block.tag ?? ''))
  ) {
    return renderEagerHtmlBlock(block);
  }
  return null;
};
